import { Stethoscope, Microscope, FileSearch, Landmark, ShieldCheck, LineChart, Database, BookOpenCheck, HeartPulse } from "lucide-react";
import { StakeholderPage } from "./StakeholderPage";

/**
 * /for/sports-medicine — the physician/clinical purchase-decision page.
 * Sports medicine buys longitudinal data quality, clinical integration, and compliance.
 */
export default function ForSportsMedicine() {
  return (
    <StakeholderPage
      testid="for-sportsmed-page"
      eyebrow="For Sports Medicine"
      EyebrowIcon={Stethoscope}
      headline="Clinical decisions deserve"
      headlineSub="more than a verbal history."
      intro={<>
        BioHacker Sports™ delivers the <b className="text-white">longitudinal exposure record</b> clinical judgment has
        always been missing: verified workload, recovery, sprint and jump baselines, and 2D/3D biomechanics on every
        athlete — structured, dated, and HIPAA-aligned. The intake interview becomes a data review.
      </>}
      risksHeading="The four gaps between the field and the clinic."
      risksSub="And what a shared data spine closes."
      risks={[
        { icon: FileSearch, label: "Presentation without exposure history", tone: "#F59E0B",
          body: "The athlete presents with a stress reaction; the chart contains nothing about the six-week loading pattern that produced it. Etiology becomes guesswork, and so does the prevention plan.",
          relief: "Complete workload and recovery timelines attach to every referral. You see the mechanism, the accumulation, and the inflection point — not just the endpoint." },
        { icon: Microscope, label: "Mechanics invisible until re-injury", tone: "#F43F5E",
          body: "The compensation pattern that caused the injury is still there at clearance — nobody has quantified it, so nobody has corrected it.",
          relief: "2D/3D biomechanics baselines and post-injury comparisons quantify asymmetry and kinematic drift. Clearance addresses the cause, not just the symptom." },
        { icon: Landmark, label: "Institutional exposure on clearance calls", tone: "#A78BFA",
          body: "Your signature is the institution's legal position. Clearance based on subjective readiness is exactly what plaintiff experts look for.",
          relief: "Objective, dated criteria behind every clearance — baseline percentages, load progression, symptom-free thresholds — create a defensible clinical record by default." },
        { icon: Database, label: "Data scattered across silos", tone: "#22D3EE",
          body: "Wearable exports, AT notes, S&C spreadsheets, imaging reports — each in its own system, none of it longitudinal, none of it research-grade.",
          relief: "One structured athlete record spanning testing, workload, wellness, and biomechanics — exportable for case review, registry reporting, or IRB-approved research." },
      ]}
      surfaceHeading="Your clinical surface."
      surfaceSub="Referral to clearance on one record."
      surface={[
        { icon: LineChart, label: "Longitudinal athlete record", body: "Verified test history, workload exposure, and recovery trends from first baseline to today — the natural history of every presentation." },
        { icon: BookOpenCheck, label: "Evidence-based RTP protocols", body: "Criteria-driven return pathways benchmarked against pre-injury baselines, with progression data captured at every stage." },
        { icon: ShieldCheck, label: "HIPAA-aligned governance", body: "Role-based access, audit trails, and SOC 2-aligned controls — athlete health data handled to clinical standards, not consumer-app standards." },
      ]}
      ctaHeading="Bring the field's data into the clinic."
      ctaSub="Book a clinically-scoped walkthrough. We'll review the athlete record model and compliance posture with your team."
      demoLabel="Request a clinical demo"
    />
  );
}
